import Card from '../components/card/Card';
import { saveClientInfo } from '../services/ClientService';
import {
  applicationInfo,
  clientData,
  documentData,
  signersData,
} from '../types/interfaces';
import BasicData from '../organisms/Origination/BasicData';
import Documents from '../organisms/Origination/Documents';
import { useEffect, useState } from 'react';
import H from '../components/texts/h/H';
import Label from '../components/texts/label/Label';
import { useLocation, useParams } from 'react-router-dom';
import Application from '../organisms/Origination/Application';
import { Toast, useToast } from '../components/toast/toast';
import Icon from '../components/icon/Icon';
import { capitalize, hasData } from '../utils/Utils';
import { initialClientData } from '../utils/Constants';
import Summary from '../organisms/Origination/Summary/Summary';
import {
  getApplication,
  sendApplication,
} from '../services/ApplicationService';
import Step from '../components/Step/Step';
import useStatus from '../hooks/useStatus';
import useStepNavigation from '../hooks/useStepNavigation';
import { cuitMask } from '../utils/Masks';
import { useSignersContext } from '../context/SignersContext';

const Origination = () => {
  const { cuit, id } = useParams();
  const { state } = useLocation();
  const { status, fromSolicitudesTab } = state ? state : '';
  const {
    isRejected,
    isApproved,
    isFinished,
    isInCommittee,
    isInInstrumentation,
  } = useStatus({
    applicationStatus: parseInt(status),
  });
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isValidatedClient, setIsValidatedClient] = useState<boolean>(true);
  const [currentStep, setCurrentStep] = useState(state ? 4 : 1);
  const [isClientDataLoaded, setIsClientDataLoaded] = useState<boolean>(false);
  const [clientData, setClientData] = useState<clientData>(initialClientData);
  const [previousStep, setPreviousStep] = useState<number>(currentStep);
  const { savedSigners, setSavedSigners } = useSignersContext();
  const [applicationExists, setApplicationExists] = useState(null);

  // Hook para manejar la navegación automática de pasos
  const {
    updateMaxStepReached,
    navigateToStep,
    goToNextStep,
    goToPreviousStep,
  } = useStepNavigation({
    cuit,
    id,
    moduleName: 'origination',
    totalSteps: 4,
    currentStep,
    setCurrentStep,
  });

  const mapCorporateToSigners = (corporateList: any[]): signersData[] => {
    return corporateList.map((member) => ({
      cuit: member.cuit || member.ci || '',
      name: member.name || member.first_name || '',
      email: member.email || '',
      asset_file_path: '',
      signer_type_code: member.signer_type_code || ['001'],
      role: member.position || '',
      position: member.position || '',
      locality: '',
      address: '',
    }));
  };
  useEffect(() => {
    (async () => {
      if (cuit) {
        const response = await getApplication(
          cuit || clientData.cuit,
          id || clientData.id || clientData.id_application?.toString() || ''
        );
        if (hasData(response)) {
          const shouldUpdate =
            (response.data.corporate_structure?.length || 0) >
              (clientData.corporate_structure?.length || 0) ||
            (response.data.officer && !clientData.officer) ||
            (response.data.referrer_name && !clientData.referrer_name);

          if (shouldUpdate) {
            setClientData(response.data);
          }
        }
      }
    })();
  }, [
    cuit,
    id,
    clientData.cuit,
    clientData.id,
    clientData.id_application,
    // Remover currentStep para evitar recargas innecesarias
  ]);

  // Caso 1: solo mostrar mensaje cuando hay solicitud vigente (id_application), no cuando es null/undefined
  useEffect(() => {
    const hasActiveApplication =
      applicationExists != null &&
      applicationExists !== '' &&
      String(applicationExists) !== '-1';
    if (hasActiveApplication) {
      useToast.success({
        title: 'Ya existe una solicitud con esta empresa.',
        id: 'save-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
    }
  }, [applicationExists]);

  // Resetear isClientDataLoaded cuando el usuario navega al paso 4
  // Esto asegura que siempre se haga un fetch fresco de los datos
  useEffect(() => {
    if (currentStep === 4) {
      setIsClientDataLoaded(false);
    }
  }, [currentStep]);

  const steps = [
    {
      id: 1,
      value: 'Datos del cliente',
      element: (
        <BasicData
          onSave={(e: any) => handleSave(e)}
          onSendStatus={(e: any) => {
            setApplicationExists(e);
          }}
          isSaving={isSaving}
          basicData={clientData}
          applicationStatus={status}
          isValidatedClient={(verify: boolean) => verifyClientData(verify)}
          currentStep={currentStep}
          previousStep={previousStep ?? currentStep}
          onDataChange={(updatedClientInfo: clientData) => {
            setClientData(updatedClientInfo);
          }}
        />
      ),
    },
    {
      id: 2,
      value: 'Carga de documentos',
      element: (
        <Documents
          onSave={(e: Array<documentData>) => handleSave(e)}
          documents={clientData.files}
          selectedLegalEntity={clientData.basic_data.legal_entity}
          cuit={clientData.cuit}
          applicationId={id}
          onPreviousClick={() => handleGoToPreviousStep()}
          isSaving={isSaving}
          applicationStatus={status}
        />
      ),
    },
    {
      id: 3,
      value: 'Solicitud',
      element: (
        <Application
          onSave={(e: Array<applicationInfo>) => handleSave(e)}
          applications={clientData.products}
          cuit={clientData.cuit}
          id={id}
          applicationStatus={parseInt(status)}
          onPreviousClick={() => handleGoToPreviousStep()}
          isSaving={isSaving}
          manualTotalAmounts={clientData.manual_total_amount}
          requestedAmounts={clientData.requested_amount}
          reason={clientData.reason}
          review={clientData.review}
          extraComment={clientData.extra_comments}
          clientData={clientData}
          signers={savedSigners}
        />
      ),
    },
    {
      id: 4,
      value: 'Resumen',
      element: (
        <Summary
          setClientData={(e: any) => {
            if (e.signers?.length === 0 && e.corporate_structure?.length > 0) {
              setSavedSigners(
                mapCorporateToSigners(e.corporate_structure) as signersData[]
              );
            }
            if (
              e.signers?.length === 0 &&
              e.corporate_structure?.length === 0
            ) {
              setSavedSigners([]);
            }
            if (e.signers?.length > 0) {
              setSavedSigners(e.signers as signersData[]);
            }
            setClientData(e);
          }}
          applicationStatus={parseInt(status)}
          onPreviousClick={
            fromSolicitudesTab ? undefined : () => goToPreviousStep()
          }
          isSaving={isSaving}
          clientData={clientData}
          onLoadClientData={(e: boolean) => setIsClientDataLoaded(e)}
          isClientDataLoaded={isClientDataLoaded}
          showButtons={true}
          showPartners={true}
          showSummaryDocuments={true}
          showValidateComposition={!fromSolicitudesTab}
          showSummaryApplication={true}
        />
      ),
    },
  ];

  const handleStep1 = (dataToAdd: any, cuit: string) => {
    if (
      dataToAdd.clientInfo.signers.length === 0 &&
      dataToAdd.clientInfo.corporate_structure.length > 0
    ) {
      setSavedSigners(
        mapCorporateToSigners(dataToAdd.clientInfo.corporate_structure)
      );
    }
    if (
      dataToAdd.clientInfo.signers.length === 0 &&
      dataToAdd.clientInfo.corporate_structure.length === 0
    ) {
      setSavedSigners([]);
    }
    if (dataToAdd.clientInfo.signers.length > 0) {
      setSavedSigners(dataToAdd.clientInfo.signers);
    }
    setClientData(dataToAdd.clientInfo);

    // Siempre guardar los cambios, incluso si la aplicación está en revisión
    saveClientInfo(dataToAdd.clientInfo, cuit || '', parseInt(id || '0')).then(
      (response) => {
        if (!isRejected && !isApproved && !isFinished) {
          handleNextStep(response.status, dataToAdd.shouldContinue);
        } else {
          goToNextStep();
          updateMaxStepReached(currentStep + 1);
          setIsSaving(false);
        }
      }
    );
  };

  const handleStep2 = (dataToAdd: any, cuit: string) => {
    // Si existe applicationId, todos los archivos van a application_files
    // Si no existe, todos los archivos van a files
    if (id) {
      // Convertir documentData a applicationFile (mapear type -> file_type, file_name -> asset_file_path)
      clientData.application_files = dataToAdd.files.map((file: documentData) => ({
        file_type: file.type,
        asset_file_path: file.file_name,
      }));
    } else {
      // Cuando no existe applicationId, todos los archivos (básicos y extras) van a files
      clientData.files = dataToAdd.files;
    }
    
    if (!isRejected && !isApproved && !isFinished) {
      saveClientInfo(clientData, cuit || '', parseInt(id || '0')).then(
        (response) => {
          handleNextStep(response.status, dataToAdd.shouldContinue);
        }
      );
    } else {
      goToNextStep();
      updateMaxStepReached(currentStep + 1);
      setIsSaving(false);
    }
  };

  const handleStep3 = (dataToAdd: any, cuit: string) => {
    if (
      !isRejected &&
      !isApproved &&
      !isFinished &&
      !isInCommittee &&
      !isInInstrumentation
    ) {
      const updatedClientData = {
        ...clientData,
        products: dataToAdd.applications,
        manual_total_amount: dataToAdd.manualTotalAmounts,
        requested_amount: dataToAdd.requestedAmounts,
        reason: dataToAdd.reason,
        review: dataToAdd.review,
        extra_comments: dataToAdd.extraComment,
        corporate_structure: dataToAdd.corporateStructure,
        signers: dataToAdd.signers,
        effective_date: dataToAdd.effective_date,
        credit_type_id: dataToAdd.credit_type_id,
        credit_type_name: dataToAdd.credit_type_name,
      };
      setClientData(updatedClientData);
      if (id?.length && id?.length > 0) {
        saveClientInfo(updatedClientData, cuit, parseInt(id || '0')).then((response) => {
          handleNextStep(response.status, dataToAdd.shouldContinue);
        });

        if (
          dataToAdd.signers?.length === 0 &&
          dataToAdd.corporate_structure?.length > 0
        ) {
          setSavedSigners(mapCorporateToSigners(dataToAdd.corporate_structure));
        }
        if (
          dataToAdd.signers?.length === 0 &&
          dataToAdd.corporate_structure?.length === 0
        ) {
          setSavedSigners([]);
        }
        if (dataToAdd.signers?.length > 0) {
          setSavedSigners(dataToAdd.signers);
        }
      } else {
        saveClientInfo(updatedClientData, cuit, parseInt(id || '0')).then(
          (response) => {
            handleNextStep(response.status, dataToAdd.shouldContinue);
          }
        );
        if (
          dataToAdd.signers?.length === 0 &&
          dataToAdd.corporate_structure?.length > 0
        ) {
          setSavedSigners(mapCorporateToSigners(dataToAdd.corporate_structure));
        }
        if (
          dataToAdd.signers?.length === 0 &&
          dataToAdd.corporate_structure?.length === 0
        ) {
          setSavedSigners([]);
        }
        if (dataToAdd.signers?.length > 0) {
          setSavedSigners(dataToAdd.signers);
        }
      }
    } else {
      goToNextStep();
      updateMaxStepReached(currentStep + 1);
      setIsSaving(false);
    }
  };

  const handleSave = (dataToAdd: any) => {
    setIsSaving(true);
    const cuit: string = dataToAdd.clientInfo?.cuit ?? clientData.cuit;
    switch (currentStep) {
      case 1:
        handleStep1(dataToAdd, cuit);
        break;
      case 2:
        handleStep2(dataToAdd, cuit);
        break;
      case 3:
        handleStep3(dataToAdd, cuit);
        break;
    }
  };

  const handleNextStep = (
    status: number | undefined,
    shouldContinue: boolean
  ) => {
    if (status === 200) {
      useToast.success({
        title: 'Guardado con éxito',
        id: 'save-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
    } else {
      useToast.error({
        title: 'Ocurrió un error al guardar la información.',
        id: 'save-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
    }
    if (shouldContinue) {
      goToNextStep();
      updateMaxStepReached(currentStep + 1);
    }
    setIsSaving(false);
  };
  const verifyClientData = (isVerified: boolean) => {
    setIsValidatedClient(isVerified);
  };

  const changeStep = (step: number) => {
    if (currentStep > step) {
      setPreviousStep(currentStep);
      navigateToStep(step);
    }
  };

  const handleGoToPreviousStep = () => {
    setPreviousStep(currentStep);
    goToPreviousStep();
  };

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing={'4'}
      height='-webkit-fill-available'
      width='85vw'
    >
      <Toast id='save-toast' />
      <Card display='flex' margin={{ left: '2' }}>
        <H withMargin={false} type='2' weight='bold' modifier='strong'>
          {clientData.cuit
            ? 'Solicitud de ' +
              capitalize(clientData.basic_data.company_name) +
              ' ( ' +
              cuitMask(clientData.cuit) +
              ' )'
            : 'Alta de nueva carpeta'}
        </H>
      </Card>
      {(clientData.last_comment || !isValidatedClient) && (
        <Card
          display='flex'
          margin={{ top: '5' }}
          spacing={'3'}
          variant='warning'
          modifier='weak'
          gap='8px'
          borderRadiusAmount='4'
        >
          <Icon
            name='warning_triangle_outline'
            variant='warning'
            width={32}
            height={32}
          />
          <Card display='grid'>
            {!isValidatedClient && (
              <Card display='flex' margin={{ top: '2' }}>
                <Label type='1' variant='neutral' weight='bold'>
                  &bull;&nbsp;
                </Label>
                <Label type='1' variant='neutral' weight='bold'>
                  No se pudieron validar los datos:&nbsp;{' '}
                </Label>
                <Label type='1' variant='neutral'>
                  {' '}
                  Cargalos manualmente e intentá nuevamente
                </Label>
              </Card>
            )}
            {clientData.last_comment && (
              <Card display='flex' margin={{ top: '2' }}>
                {!isValidatedClient && (
                  <Label type='1' variant='neutral' weight='bold'>
                    &bull;&nbsp;
                  </Label>
                )}
                <Label type='1' variant='neutral' weight='bold'>
                  Cambios solicitados:&nbsp;
                </Label>
                <Label type='1' variant='neutral'>
                  {clientData.last_comment}
                </Label>
              </Card>
            )}
          </Card>
        </Card>
      )}
      <Card
        display='flex'
        justifyContent='center'
        margin={{ top: '4', bottom: '4' }}
        width='100%'
      >
        {steps.map((step, index) => (
          <Step
            widthContainer='200px'
            widthCircle1='81px'
            widthId='20px'
            heightId='20px'
            widthCircle2='80px'
            step={step}
            stepsLength={steps.length}
            index={index}
            currentStep={currentStep}
            changeStep={(step) => changeStep(step)}
          />
        ))}
      </Card>
      {steps[currentStep - 1].element}
    </Card>
  );
};

export default Origination;
